import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PieChart as PieChartIcon } from 'lucide-react'
import {
  Area, AreaChart, Bar, BarChart, Cell, Legend, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts'
import { sectorShiftApi } from '../api/client'
import { ChartCard } from './ChartCard'
import { approveCopy } from '../lib/sectorShift'
import type {
  SectorContext, SectorExposure, SectorHistory, SectorShiftConfig, SectorShiftName,
  SectorShiftProposal, SectorShiftRange, SectorShiftRuns, SectorShiftVerdict,
} from '../types'
import './SectorPanel.css'

interface SectorPanelProps {
  accountId: number
}

// Spec §3.3: a group is a shed candidate at/above this book weight. The live value comes
// from GET /sector-shift/config (`min_from_weight`); this is only the fallback while that
// read is loading or has failed (matches the backend default, lowered 2026-10-09).
const DEFAULT_MIN_FROM_WEIGHT = 0.2
const fmtBar = (v: number): string => `${(v * 100).toFixed(0)}%`
const RANGES: SectorShiftRange[] = ['1M', '3M', '6M', '1Y', 'ALL']
const SERIES_COLORS = [
  'var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)',
  'var(--series-5)', 'var(--series-6)', 'var(--series-7)', 'var(--series-8)',
]
// Confidence components in spec order with the abbreviations shown in the verdict table.
const COMPONENTS = [
  ['margin', 'm'], ['upgrade', 'u'], ['context', 'c'], ['completeness', 'k'],
] as const
const EVENT_LABEL: Record<string, string> = {
  executed: 'exec', exit_filled: 'exit', entry_filled: 'entry', partial: 'partial',
  aborted: 'abort', rejected: 'rej', expired: 'exp',
}

const names = (items: SectorShiftName[] | null | undefined): string =>
  (items ?? []).map((n) => n.symbol ?? String(n)).join(', ') || '—'

const fmtUSD = (v: number | null | undefined): string =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtPct = (v: number): string => `${(v * 100).toFixed(1)}%`
const fmtDate = (s: string | null): string => (s ? s.slice(0, 10) : '—')
// Viewer-local timestamps (the rest of the dashboard uses toLocale* inline the same way).
const fmtLocalDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}
const fmtLocalTime = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
}
const reasonLabel = (r: string | null): string => (r ? r.replace(/_/g, ' ') : '')

/** Max across books of the per-book exit-tax estimate (the evaluator's tax_cost). */
const maxTax = (byBook: Record<string, number> | null | undefined): number | null => {
  const vals = Object.values(byBook ?? {}).filter((v) => Number.isFinite(v))
  return vals.length ? Math.max(...vals) : null
}

/** Snap an event timestamp to the first plotted date >= it (else the last), so a
 * ReferenceLine always lands on a category that exists on the AreaChart x axis. */
function snapToPoint(t: string, dates: string[]): string | null {
  if (!dates.length) return null
  const day = t.slice(0, 10)
  return dates.find((d) => d >= day) ?? dates[dates.length - 1]
}

function UnknownBadge({ n }: { n: number }) {
  if (n <= 0) return null
  return <span className="sector-badge sector-badge--warn" title="Names whose sector could not be resolved">Unknown: {n}</span>
}

function ExposureChart({ data, minFromWeight }: { data: SectorExposure | undefined; minFromWeight: number }) {
  const rows = (data?.by_group ?? []).map((g) => ({
    group: g.group, weight: Number(g.weight), count: g.count,
  }))
  return (
    <div className="sector-block">
      <div className="sector-block-head">
        <h4 className="sector-block-title">Exposure by industry group</h4>
        <span className="sector-badges">
          <UnknownBadge n={data?.unknown_count ?? 0} />
          {data?.answer_key_match != null && (
            <span className={`sector-badge ${data.answer_key_match ? 'sector-badge--ok' : 'sector-badge--warn'}`}>
              answer key {data.answer_key_match ? 'match' : 'MISMATCH'}
            </span>
          )}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="caption">no positions</p>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(120, rows.length * 30 + 40)}>
          <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 16 }}>
            <XAxis type="number" domain={[0, 'auto']} stroke="var(--fg-subtle)" fontSize={11}
              tickFormatter={(v: number) => fmtPct(v)} />
            <YAxis type="category" dataKey="group" width={170} stroke="var(--fg-subtle)" fontSize={11} />
            <Tooltip formatter={(v: number) => fmtPct(v)} />
            <ReferenceLine x={minFromWeight} stroke="var(--warn)" strokeDasharray="4 3"
              label={{ value: fmtBar(minFromWeight), fill: 'var(--warn-fg)', fontSize: 11, position: 'top' }} />
            <Bar dataKey="weight" name="weight">
              {rows.map((r) => (
                <Cell key={r.group} fill={r.weight >= minFromWeight ? 'var(--warn)' : 'var(--series-1)'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

function ContextChart({ data, isLoading, isError }: {
  data: SectorContext | undefined; isLoading: boolean; isError: boolean
}) {
  // 63d relative return vs SPY per sector / group ETF, in PERCENT units.
  const rows = (data?.sectors ?? [])
    .filter((s) => s.rel_ret['63'] != null)
    .map((s) => ({ label: s.group ?? s.sector, etf: s.etf, rel: Number(s.rel_ret['63']) * 100 }))
    .sort((a, b) => a.rel - b.rel)
  return (
    <div className="sector-block">
      <div className="sector-block-head">
        <h4 className="sector-block-title">Sector vs market · 63d relative return</h4>
        <span className="sector-badges">
          <UnknownBadge n={data?.unknown_symbols.length ?? 0} />
        </span>
      </div>
      {isLoading ? (
        <p className="caption">loading context…</p>
      ) : isError ? (
        <p className="caption">context unavailable</p>
      ) : rows.length === 0 ? (
        <p className="caption">no context data yet</p>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(140, rows.length * 26 + 40)}>
          <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 16 }}>
            <XAxis type="number" stroke="var(--fg-subtle)" fontSize={11}
              tickFormatter={(v: number) => `${v.toFixed(0)}%`} />
            <YAxis type="category" dataKey="label" width={170} stroke="var(--fg-subtle)" fontSize={11} />
            <Tooltip formatter={(v: number) => `${v.toFixed(2)}% vs SPY`} />
            <ReferenceLine x={0} stroke="var(--border-strong)" />
            <Bar dataKey="rel" name="vs SPY (63d)">
              {rows.map((r) => (
                <Cell key={r.label} fill={r.rel >= 0 ? 'var(--gain)' : 'var(--loss)'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      {(data?.unknown_symbols.length ?? 0) > 0 && (
        <p className="caption">no sector for: {data?.unknown_symbols.join(', ')}</p>
      )}
    </div>
  )
}

function HistoryChart({ accountId }: { accountId: number }) {
  const [range, setRange] = useState<SectorShiftRange>('3M')
  const { data, isLoading, isError } = useQuery<SectorHistory>({
    queryKey: ['sector-shift', 'history', accountId, range],
    queryFn: () => sectorShiftApi.history(accountId, range),
    retry: 1,
    staleTime: 300000,
  })
  const points = data?.points ?? []
  const groups = Array.from(new Set(points.flatMap((p) => p.by_group.map((g) => g.group))))
  const rows = points.map((p) => {
    const row: Record<string, string | number> = { t: p.t }
    for (const g of groups) row[g] = 0
    for (const g of p.by_group) row[g.group] = Number(g.weight)
    return row
  })
  const dates = rows.map((r) => String(r.t))
  const markers = (data?.events ?? [])
    .map((e) => ({ e, x: snapToPoint(e.t, dates) }))
    .filter((m): m is { e: typeof m.e; x: string } => m.x !== null)
  return (
    <div className="sector-block">
      <div className="sector-block-head">
        <h4 className="sector-block-title">Exposure history</h4>
        <span className="sector-range" role="group" aria-label="history range">
          {RANGES.map((r) => (
            <button key={r} type="button" aria-pressed={r === range}
              className={`sector-range-btn${r === range ? ' is-active' : ''}`}
              onClick={() => setRange(r)}>{r}</button>
          ))}
        </span>
      </div>
      {isLoading ? (
        <p className="caption">loading history…</p>
      ) : isError ? (
        <p className="caption">history unavailable</p>
      ) : rows.length < 2 ? (
        <p className="caption">not enough runs yet — one point is recorded per evaluator run</p>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={rows}>
            <XAxis dataKey="t" stroke="var(--fg-subtle)" fontSize={11} />
            <YAxis stroke="var(--fg-subtle)" fontSize={11} domain={[0, 1]}
              tickFormatter={(v: number) => fmtPct(v)} />
            <Tooltip formatter={(v: number) => fmtPct(v)} />
            <Legend />
            {groups.map((g, i) => (
              <Area key={g} type="monotone" dataKey={g} stackId="exposure"
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                fill={SERIES_COLORS[i % SERIES_COLORS.length]} fillOpacity={0.55} />
            ))}
            {markers.map(({ e, x }, i) => (
              <ReferenceLine key={`${e.type}-${e.proposal_id ?? 'run'}-${i}`} x={x}
                stroke="var(--fg-default)" strokeDasharray="3 3"
                label={{ value: EVENT_LABEL[e.type] ?? e.type, fill: 'var(--fg-muted)', fontSize: 10, position: 'top' }} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

function ConfidenceCell({ v }: { v: number | null }) {
  if (v == null) return <span className="muted">—</span>
  const pct = Math.max(0, Math.min(1, v)) * 100
  return (
    <span className="sector-conf" title={`confidence ${v.toFixed(3)}`}>
      <span className="sector-conf-track"><span className="sector-conf-fill" style={{ width: `${pct}%` }} /></span>
      <span className="num">{v.toFixed(2)}</span>
    </span>
  )
}

/** "Next run …" copy from the schedule (I7 part 4). */
function nextRunLine(sch: SectorShiftRuns['schedule']): string {
  if (sch.clock_source !== 'broker') {
    return 'Next run: next trading day after the open-bell tick (broker clock unavailable)'
  }
  if (sch.market_is_open && !sch.today_attempted) return 'Next run today, after the open-bell tick'
  if (sch.expected_run_at) return `Next run ${fmtLocalDateTime(sch.expected_run_at)}, after the open-bell tick`
  if (sch.next_open) return `Next run after the ${fmtLocalDateTime(sch.next_open)} open, after the open-bell tick`
  return 'Next run: next trading day after the open-bell tick'
}

function lastRunLine(data: SectorShiftRuns): string {
  const d = data.days[0]
  if (d) return `Last run ${fmtLocalDateTime(d.completed_at)} · ${d.status} · ${d.n_verdicts} verdicts`
  if (data.schedule.last_run_at) return `Last run ${fmtLocalDateTime(data.schedule.last_run_at)}`
  return 'no runs yet'
}

function RunsBlock({ data, isLoading, isError }: {
  data: SectorShiftRuns | undefined; isLoading: boolean; isError: boolean
}) {
  return (
    <div className="sector-block">
      <h4 className="sector-block-title">Evaluator runs</h4>
      {isLoading && <p className="caption">loading runs…</p>}
      {isError && <p className="caption">run status unavailable</p>}
      {data && (
        <>
          <div className="sector-run-strip" data-testid="sector-run-strip">
            <span>{lastRunLine(data)}</span>
            <span>{nextRunLine(data.schedule)}</span>
          </div>
          {data.days.length > 0 && (
            <div className="sector-scroll">
              <table className="sector-table">
                <thead>
                  <tr><th>Date</th><th>Status</th><th>Verdicts / Proposals</th><th>Attempts</th></tr>
                </thead>
                <tbody>
                  {data.days.map((d) => (
                    <tr key={d.run_id}>
                      <td className="mono">{fmtDate(d.as_of)}{d.trigger !== 'cycle' && <span className="sector-badge">manual</span>}</td>
                      <td>
                        <span className={`sector-status sector-status--${d.status}`} title={d.abort_reason ?? undefined}>
                          {d.status}
                        </span>
                      </td>
                      <td className="num">{d.n_verdicts} / {d.n_proposals}</td>
                      <td className="sector-run-attempts">
                        {d.attempts.length}: {d.attempts.map((a) =>
                          `${fmtLocalTime(a.at)} ${a.status}${a.reason ? ` (${reasonLabel(a.reason)})` : ''}`).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function VerdictsTable({ rows }: { rows: SectorShiftVerdict[] }) {
  return (
    <div className="sector-block">
      <h4 className="sector-block-title">Verdicts</h4>
      {rows.length === 0 ? <p className="caption">no verdicts yet</p> : (
        <div className="sector-scroll">
          <table className="sector-table">
            <thead>
              <tr>
                <th>Date</th><th>Routing</th><th>From → To</th><th>Value</th>
                <th>Cost</th><th>Tax</th><th>Confidence</th><th>Components</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => {
                const c = v.components ?? {}
                // S-9 dedup: an AUTO/PROPOSAL verdict recorded without its own
                // proposal because an executable one for the same pair was active
                const dedupedBehind = c.deduped_behind
                return (
                  <tr key={v.id}>
                    <td className="mono">{fmtDate(v.created_at)}</td>
                    <td>
                      <span className={`sector-route sector-route--${v.routing.toLowerCase()}`}>{v.routing}</span>
                      {dedupedBehind != null && (
                        <span className="sector-badge"
                          title={`No new proposal: active proposal #${dedupedBehind} covers the same shift`}>
                          deduped behind #{dedupedBehind}
                        </span>
                      )}
                    </td>
                    <td>{v.from_group} → {v.to_sector}</td>
                    <td className="num">{fmtUSD(v.value)}</td>
                    <td className="num">{fmtUSD(v.cost)}</td>
                    <td className="num">{fmtUSD(maxTax(v.tax_cost_by_book))}</td>
                    <td><ConfidenceCell v={v.confidence} /></td>
                    <td className="mono sector-components"
                      title="m margin · u upgrade · c context · k completeness">
                      {COMPONENTS.map(([k, abbr]) =>
                        `${abbr} ${c[k] != null ? Number(c[k]).toFixed(2) : '—'}`).join(' · ')}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ProposalsQueue({ rows, accountId }: { rows: SectorShiftProposal[]; accountId: number }) {
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState<number | null>(null)
  const invalidate = () => qc.invalidateQueries({ queryKey: ['sector-shift'] })
  const approve = useMutation({
    mutationFn: (id: number) => sectorShiftApi.approve(id, accountId),
    onSuccess: () => { setConfirming(null); invalidate() },
  })
  const reject = useMutation({
    mutationFn: (id: number) => sectorShiftApi.reject(id, accountId),
    onSuccess: invalidate,
  })
  const err = (approve.error ?? reject.error) as Error | null
  return (
    <div className="sector-block">
      <h4 className="sector-block-title">Proposals</h4>
      {err && <div className="sector-error" role="alert">action failed: {err.message}</div>}
      {rows.length === 0 ? <p className="caption">no proposals</p> : (
        <div className="sector-scroll">
          <table className="sector-table">
            <thead>
              <tr>
                <th>Status</th><th>Shed</th><th>Targets</th><th>Confidence</th>
                <th>Expires</th><th>Abort reason</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td><span className={`sector-status sector-status--${p.status}`}>{p.status}</span>{p.auto && <span className="sector-badge">auto</span>}</td>
                  <td className="mono">{names(p.shed)}</td>
                  <td className="mono">{names(p.targets)}</td>
                  <td><ConfidenceCell v={p.confidence} /></td>
                  <td className="mono">{fmtDate(p.expires_at)}</td>
                  <td>{p.abort_reason ?? <span className="muted">—</span>}</td>
                  <td className="sector-actions">
                    {confirming === p.id ? (
                      <div className="sector-confirm" role="group" aria-label={`confirm approve ${p.id}`}>
                        <p className="caption">{approveCopy(names(p.shed), names(p.targets))}</p>
                        <button type="button" className="btn btn-sm btn-primary"
                          disabled={approve.isPending}
                          onClick={() => approve.mutate(p.id)}>Confirm approve</button>
                        <button type="button" className="btn btn-sm btn-ghost"
                          onClick={() => setConfirming(null)}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        {p.status === 'pending' && (
                          <button type="button" className="btn btn-sm btn-primary"
                            aria-label={`Approve proposal ${p.id}`}
                            onClick={() => setConfirming(p.id)}>Approve</button>
                        )}
                        {(p.status === 'pending' || p.status === 'approved') && (
                          <button type="button" className="btn btn-sm btn-danger"
                            aria-label={`Reject proposal ${p.id}`}
                            disabled={reject.isPending}
                            onClick={() => reject.mutate(p.id)}>Reject</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * Sector panel (wargame 21 I6): is the book's sector exposure intentional?
 * (0) the evaluator run log + next-run schedule (I7 part 4; never blocks the panel),
 * (a) exposure by industry group vs the shed bar (live min_from_weight), (b) sector-vs-market context
 * (the "semis weak vs market" instrument), (c) exposure history with shift events,
 * (d) evaluator verdicts, (e) the proposal queue (approve/reject are admin-session
 * only server-side). Decimal strings are Number()-ed only at the chart boundary.
 *
 * The card's loading/error/empty ladder is over ALL the primary reads (exposure,
 * verdicts, proposals): a broker outage (exposure 503) must not hide the proposal
 * queue the operator needs, so error is "everything failed" and empty is "nothing
 * anywhere"; each section degrades inline on its own.
 */
export function SectorPanel({ accountId }: SectorPanelProps) {
  const exposure = useQuery<SectorExposure>({
    queryKey: ['sector-shift', 'exposure', accountId],
    queryFn: () => sectorShiftApi.exposure(accountId),
    retry: 1,
    staleTime: 60000,
  })
  // Never blocks the panel: while loading or errored the 0.2 fallback is used.
  const config = useQuery<SectorShiftConfig>({
    queryKey: ['sector-shift', 'config', accountId],
    queryFn: () => sectorShiftApi.config(accountId),
    retry: 1,
    staleTime: 300000,
  })
  const liveBar = Number(config.data?.min_from_weight)
  const minFromWeight = Number.isFinite(liveBar) && liveBar > 0 ? liveBar : DEFAULT_MIN_FROM_WEIGHT
  const context = useQuery<SectorContext>({
    queryKey: ['sector-shift', 'context', accountId],
    queryFn: () => sectorShiftApi.context(accountId),
    retry: 1,
    staleTime: 300000,
  })
  const verdicts = useQuery<SectorShiftVerdict[]>({
    queryKey: ['sector-shift', 'verdicts', accountId],
    queryFn: () => sectorShiftApi.verdicts(accountId, 50),
    retry: 1,
    refetchInterval: 30000,
  })
  const proposals = useQuery<SectorShiftProposal[]>({
    queryKey: ['sector-shift', 'proposals', accountId],
    queryFn: () => sectorShiftApi.proposals(accountId),
    retry: 1,
    refetchInterval: 30000,
  })
  // Never blocks the panel: loading / error degrade inline inside the runs block.
  const runs = useQuery<SectorShiftRuns>({
    queryKey: ['sector-shift', 'runs', accountId],
    queryFn: () => sectorShiftApi.runs(accountId, 7),
    retry: 1,
    refetchInterval: 60000,
  })

  const isLoading = exposure.isLoading
  const isError = exposure.isError && verdicts.isError && proposals.isError
  const isEmpty =
    (exposure.data?.by_group.length ?? 0) === 0 &&
    (verdicts.data?.length ?? 0) === 0 &&
    (proposals.data?.length ?? 0) === 0 &&
    (runs.data?.days.length ?? 0) === 0

  return (
    <ChartCard
      className="sector-panel"
      title={
        <>
          <PieChartIcon size={18} className="icon" aria-hidden="true" />
          Sector exposure
        </>
      }
      headerExtra={<span className="caption">{`shed bar ${fmtBar(minFromWeight)} per group`}</span>}
      isLoading={isLoading}
      isError={isError}
      errorText="sector data unavailable"
      isEmpty={isEmpty}
      emptyText="no sector exposure or shifts yet"
      emptyIcon={<PieChartIcon size={28} aria-hidden="true" />}
    >
      <RunsBlock data={runs.data} isLoading={runs.isLoading} isError={runs.isError} />
      {exposure.isError ? (
        <div className="sector-block"><p className="caption">exposure unavailable (broker or sector map)</p></div>
      ) : (
        <ExposureChart data={exposure.data} minFromWeight={minFromWeight} />
      )}
      <ContextChart data={context.data} isLoading={context.isLoading} isError={context.isError} />
      <HistoryChart accountId={accountId} />
      <VerdictsTable rows={verdicts.data ?? []} />
      <ProposalsQueue rows={proposals.data ?? []} accountId={accountId} />
    </ChartCard>
  )
}
